import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import {
  Crosshair, Eye, EyeOff, Hand, Layers, Loader2, MapPin, Pencil, Plus, Save, SearchX, Trash2, Trees, TriangleAlert, X,
} from 'lucide-react';
import * as api from '../worlds.api';
import { toast, useWorldsStore } from '../worlds.store';
import { MAX_BLOCK_COORD, fmtDate, fmtInt, loadPref, savePref } from '../constants';
import type { Biome, Marker } from '../worlds.types';
import { useWorldMap } from '../map/useWorldMap';
import { MapFrame } from '../map/MapFrame';
import { latLngToBlock } from '../map/coords';
import { buildMarkers } from '../map/overlays';
import { BiomeIcon, BiomeModal } from '../components/BiomeModal';
import { confirmAction } from '../components/ConfirmDialog';
import { worldsModalOpen } from '../components/Modal';
import { ActionButton, CardTitle, ColorChip, EmptyState, Kbd, SearchField, StatusBadge, cx } from '../components/ui';
import styles from '../../LootboxPage.module.scss';
import ws from '../WorldsPage.module.scss';
import lm from '../levelsMarkers.module.scss';

// ============================================================
// Onglet Marqueurs : marqueurs de biomes (coordonnées BLOC) sur le niveau courant
// ============================================================
// Déplacer (V) : glisser un marqueur (PATCH x/z, retour à sa place si refus). Placer (P) : clic
// sur la carte avec le biome choisi dans la palette. La palette et la liste « Biome » d'un marqueur
// ne proposent que les biomes de ce niveau (level vide ou égal), seuls acceptés par le satellite.
// Un marqueur dont le biome est absent ou désactivé sera ignoré par le jeu : il est listé à part
// pour être nettoyé. Aucun flux ne sert encore les marqueurs au jeu (le mod les lira plus tard).
// ============================================================

type Mode = 'move' | 'place';

const MODES: { key: Mode; label: string; kbd: string; icon: typeof Hand; hint: string }[] = [
  { key: 'move', label: 'Déplacer', kbd: 'V', icon: Hand, hint: 'Glisser la carte ou un marqueur pour le déplacer. Clic sur un marqueur : ses détails.' },
  { key: 'place', label: 'Placer', kbd: 'P', icon: MapPin, hint: 'Clic sur la carte : pose un marqueur du biome choisi dans la palette.' },
];

/** Pastille d'un marqueur dont le biome n'existe plus (même gris que sur la carte). */
const ABSENT_COLOR = '#9CA3AF';

/** Préférence : pays affichés sous les marqueurs. */
const COUNTRIES_PREF = 'worlds.markers.countries';
/** Préférence : image fixe du terrain sous les pays (niveau sans tuiles qui en a une). */
const BACKGROUND_PREF = 'worlds.markers.background';

function isTyping(e: KeyboardEvent): boolean {
  const t = e.target as HTMLElement | null;
  return !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
}

function biomeLabel(b: Biome | undefined, fallback: string): string {
  return b ? b.displayNameFr || b.name : fallback;
}

export function MarkersTab({ readOnly }: { readOnly: boolean }) {
  const markers = useWorldsStore((s) => s.markers);
  const markersLevel = useWorldsStore((s) => s.markersLevel);
  const biomes = useWorldsStore((s) => s.biomes);
  const biomesVersion = useWorldsStore((s) => s.biomesVersion);
  const levels = useWorldsStore((s) => s.levels);
  const level = useWorldsStore((s) => s.level);
  const putMarker = useWorldsStore((s) => s.putMarker);
  const dropMarker = useWorldsStore((s) => s.dropMarker);
  const putBiome = useWorldsStore((s) => s.putBiome);
  const dropBiome = useWorldsStore((s) => s.dropBiome);

  const [mode, setMode] = useState<Mode>('move');
  const [biomeName, setBiomeName] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [editBiome, setEditBiome] = useState<Biome | 'new' | null>(null);
  const [draft, setDraft] = useState<{ x: string; z: string }>({ x: '', z: '' });
  const [savingSel, setSavingSel] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [showCountries, setShowCountries] = useState(() => loadPref<boolean>(COUNTRIES_PREF, true) !== false);
  const [showBackground, setShowBackground] = useState(() => loadPref<boolean>(BACKGROUND_PREF, true) !== false);

  const wm = useWorldMap({
    showChunks: showCountries, showBorders: false, showRegions: false, showBackground, chunkAlpha: 0.3, labelClass: ws.regionLabel,
  });
  const { map } = wm;

  // ---- Données dérivées ----
  const biomeMap = useMemo(() => new Map(biomes.map((b) => [b.name, b])), [biomes]);
  /** Biomes acceptés par le satellite sur ce niveau (MarkerService : level vide ou égal). */
  const levelBiomes = useMemo(() => biomes.filter((b) => b.level == null || b.level === level), [biomes, level]);
  const hiddenBiomes = biomes.length - levelBiomes.length;
  const search = q.trim().toLowerCase();
  const palette = useMemo(
    () => levelBiomes.filter((b) => !search || [b.name, b.displayNameFr, b.displayNameEn].some((v) => v.toLowerCase().includes(search))),
    [levelBiomes, search],
  );
  const countByBiome = useMemo(() => {
    const m = new Map<string, number>();
    markers.forEach((mk) => m.set(mk.biomeName, (m.get(mk.biomeName) ?? 0) + 1));
    return m;
  }, [markers]);
  const orphans = useMemo(() => markers.filter((m) => { const b = biomeMap.get(m.biomeName); return !b || !b.enabled; }), [markers, biomeMap]);
  const selected = selectedId ? markers.find((m) => m.id === selectedId) ?? null : null;
  const selectedBiome = selected ? biomeMap.get(selected.biomeName) : undefined;
  const levelTitle = levels.find((l) => l.name === level)?.title ?? level ?? '';
  const markersLoading = !!level && markersLevel !== level;
  const biomesLoading = biomesVersion === -1;
  const placeBiome = biomeName ? biomeMap.get(biomeName) : undefined;

  // Coordonnées éditables : recalées sur le marqueur choisi (et sur un déplacement réussi).
  useEffect(() => {
    setDraft(selected ? { x: String(selected.x), z: String(selected.z) } : { x: '', z: '' });
  }, [selected?.id, selected?.x, selected?.z]); // eslint-disable-line react-hooks/exhaustive-deps

  // Changement de niveau : la sélection et un biome réservé à l'ancien niveau n'ont plus de sens.
  useEffect(() => {
    setSelectedId(null);
    setBiomeName((n) => {
      const b = n ? useWorldsStore.getState().biomes.find((x) => x.name === n) : undefined;
      return b && (b.level == null || b.level === level) ? n : null;
    });
  }, [level]);

  const live = useRef({ mode, biomeName, readOnly, level, selectedId });
  live.current = { mode, biomeName, readOnly, level, selectedId };

  // ---- Écritures en cours (badge « En cours… » sur la carte) ----
  const pendingRef = useRef(0);
  const [pending, setPending] = useState(0);
  const track = useCallback(async <T,>(p: Promise<T>): Promise<T> => {
    pendingRef.current++;
    setPending(pendingRef.current);
    try {
      return await p;
    } finally {
      pendingRef.current--;
      setPending(pendingRef.current);
    }
  }, []);

  // ---- Placement au clic ----
  useEffect(() => {
    if (!map) return;
    const onClick = async (e: L.LeafletMouseEvent) => {
      const v = live.current;
      if (v.readOnly || v.mode !== 'place' || !v.level) return;
      if (!v.biomeName) { toast.warning('Choisis d\'abord un biome dans la palette.'); return; }
      const { x, z } = latLngToBlock(e.latlng);
      const res = await track(api.createMarker(v.level, { x, z, biomeName: v.biomeName }));
      if (!res.data) { toast.error(res.error ?? 'Création en échec'); return; }
      putMarker(res.data);
      setSelectedId(res.data.id);
      toast.success(`Marqueur ${biomeLabel(useWorldsStore.getState().biomes.find((b) => b.name === v.biomeName), v.biomeName)} posé en ${x}, ${z}`);
    };
    map.on('click', onClick);
    return () => { map.off('click', onClick); };
  }, [map, putMarker, track]);

  useEffect(() => {
    const el = wm.containerRef.current;
    if (el) el.style.cursor = mode === 'place' && !readOnly ? 'crosshair' : '';
  }, [mode, readOnly, wm.containerRef, map]);

  // ---- Raccourcis : V déplacer, P placer (hors saisie, hors modale) ----
  useEffect(() => {
    if (readOnly) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || isTyping(e) || worldsModalOpen()) return;
      const k = e.key.toLowerCase();
      if (k === 'v') setMode('move');
      else if (k === 'p') setMode('place');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [readOnly]);

  // Échap (via la carte) : désélectionne le marqueur avant de quitter le plein écran.
  const onEscape = useCallback(() => {
    if (!live.current.selectedId) return false;
    setSelectedId(null);
    return true;
  }, []);

  // ---- Marqueurs ----
  const groupRef = useRef<L.LayerGroup | null>(null);
  useEffect(() => {
    if (!map) return;
    const g = L.layerGroup().addTo(map);
    groupRef.current = g;
    return () => { g.remove(); groupRef.current = null; };
  }, [map]);

  const move = useCallback(async (m: Marker, x: number, z: number): Promise<boolean> => {
    if (x === m.x && z === m.z) return true;
    const res = await track(api.patchMarker(m.id, { x, z }));
    if (!res.data) { toast.error(res.error ?? 'Déplacement en échec'); return false; }
    putMarker(res.data);
    toast.success(`Marqueur déplacé en ${x}, ${z}`);
    return true;
  }, [putMarker, track]);

  useEffect(() => {
    const g = groupRef.current;
    if (!g) return;
    buildMarkers(g, markers, biomeMap, {
      className: ws.markerDot,
      selectedId,
      draggable: !readOnly && mode === 'move',
      handlers: { onSelect: (m) => setSelectedId(m.id), onMove: move },
    });
  }, [markers, biomeMap, selectedId, readOnly, mode, move, map]);

  const focus = (m: Marker) => wm.focusBlock(m.x, m.z, 6);

  const saveDraft = async () => {
    if (!selected || readOnly || savingSel) return;
    const x = Number(draft.x.trim());
    const z = Number(draft.z.trim());
    if (draft.x.trim() === '' || draft.z.trim() === '' || !Number.isInteger(x) || !Number.isInteger(z)
      || Math.abs(x) > MAX_BLOCK_COORD || Math.abs(z) > MAX_BLOCK_COORD) {
      toast.error('X et Z : entiers, 30 000 000 au plus en valeur absolue');
      return;
    }
    setSavingSel(true);
    await move(selected, x, z);
    setSavingSel(false);
  };

  const setBiomeOf = async (m: Marker, name: string) => {
    if (name === m.biomeName) return;
    setSavingSel(true);
    const res = await track(api.patchMarker(m.id, { biomeName: name }));
    setSavingSel(false);
    if (!res.data) { toast.error(res.error ?? 'Modification en échec'); return; }
    putMarker(res.data);
    toast.success(`Biome du marqueur : ${biomeLabel(biomeMap.get(name), name)}`);
  };

  const remove = async (m: Marker) => {
    const b = biomeMap.get(m.biomeName);
    const ok = await confirmAction({
      title: 'Supprimer le marqueur',
      message: <p>Supprimer le marqueur <strong>{biomeLabel(b, m.biomeName)}</strong> en {m.x}, {m.z} ?</p>,
      details: `Niveau ${levelTitle}. Le marqueur disparaît de la carte, son biome reste au catalogue.`,
    });
    if (!ok) return;
    setRemoving(m.id);
    const res = await track(api.deleteMarker(m.id));
    setRemoving(null);
    if (res.error) { toast.error(res.error); return; }
    dropMarker(m.id);
    if (live.current.selectedId === m.id) setSelectedId(null);
    toast.success('Marqueur supprimé');
  };

  const pick = (b: Biome) => {
    if (biomeName === b.name) { setBiomeName(null); return; }
    setBiomeName(b.name);
    if (!readOnly) setMode('place');
  };

  const toggleCountries = () => {
    setShowCountries((v) => { savePref(COUNTRIES_PREF, !v); return !v; });
  };
  const toggleBackground = () => {
    setShowBackground((v) => { savePref(BACKGROUND_PREF, !v); return !v; });
  };

  // ---- Colonne de droite ----
  const modeHint = mode === 'place'
    ? placeBiome ? `Clic sur la carte : pose un marqueur « ${biomeLabel(placeBiome, placeBiome.name)} ».` : MODES[1].hint
    : MODES[0].hint;

  const selectCard = selected && (
    <section className={ws.card}>
      <CardTitle icon={MapPin}
        actions={<ActionButton icon={X} title="Fermer (Échap)" onClick={() => setSelectedId(null)} />}>
        Marqueur sélectionné
      </CardTitle>
      <div className={lm.selHead}>
        <ColorChip color={selectedBiome?.color ?? ABSENT_COLOR} size={16} />
        <BiomeIcon url={selectedBiome?.iconUrl} size={18} />
        <span className={lm.selName} title={selected.biomeName}>{biomeLabel(selectedBiome, selected.biomeName)}</span>
        {!selectedBiome ? (
          <StatusBadge tone="warn" icon={TriangleAlert} title="Biome retiré du catalogue : ce marqueur sera ignoré par le jeu">absent</StatusBadge>
        ) : !selectedBiome.enabled ? (
          <StatusBadge tone="warn" icon={TriangleAlert} title="Biome désactivé : ce marqueur sera ignoré par le jeu">désactivé</StatusBadge>
        ) : selectedBiome.level && selectedBiome.level !== level ? (
          <StatusBadge tone="warn" icon={TriangleAlert}
            title={`Biome réservé au niveau ${selectedBiome.level} : il ne peut plus être choisi ici, seulement conservé`}>
            autre niveau
          </StatusBadge>
        ) : null}
      </div>
      <label className={styles.field} style={{ minWidth: 0 }}>
        <span className={styles.label}>Biome</span>
        <select className={styles.input} value={selected.biomeName} disabled={readOnly || savingSel}
          onChange={(e) => void setBiomeOf(selected, e.target.value)}>
          {!selectedBiome && <option value={selected.biomeName}>{selected.biomeName} (absent)</option>}
          {selectedBiome && !levelBiomes.includes(selectedBiome) && (
            <option value={selectedBiome.name}>{biomeLabel(selectedBiome, selectedBiome.name)} (réservé à {selectedBiome.level})</option>
          )}
          {levelBiomes.map((b) => (
            <option key={b.name} value={b.name} disabled={!b.enabled && b.name !== selected.biomeName}>
              {biomeLabel(b, b.name)}{b.enabled ? '' : ' (désactivé)'}
            </option>
          ))}
        </select>
        {!readOnly && <span className={ws.fieldHint}>Biomes actifs de ce niveau seulement.</span>}
      </label>
      <form className={ws.activeCard} noValidate onSubmit={(e) => { e.preventDefault(); void saveDraft(); }}>
        <div className={ws.formGrid2}>
          <label className={styles.field} style={{ minWidth: 0 }}>
            <span className={styles.label}>X</span>
            <input className={`${styles.input} ${lm.coordInput}`} inputMode="numeric" value={draft.x} disabled={readOnly || savingSel}
              aria-label="X (bloc)" onChange={(e) => setDraft((d) => ({ ...d, x: e.target.value }))} />
          </label>
          <label className={styles.field} style={{ minWidth: 0 }}>
            <span className={styles.label}>Z</span>
            <input className={`${styles.input} ${lm.coordInput}`} inputMode="numeric" value={draft.z} disabled={readOnly || savingSel}
              aria-label="Z (bloc)" onChange={(e) => setDraft((d) => ({ ...d, z: e.target.value }))} />
          </label>
        </div>
        <div className={ws.inlineActions}>
          {!readOnly && (
            <button type="submit" className={styles.primaryBtn} title="Entrée dans X ou Z applique aussi"
              disabled={savingSel || (draft.x.trim() === String(selected.x) && draft.z.trim() === String(selected.z))}>
              {savingSel ? <Loader2 size={13} className={styles.spin} /> : <Save size={13} />} Appliquer
            </button>
          )}
          <button type="button" className={styles.secondaryBtn} onClick={() => focus(selected)}><Crosshair size={13} /> Centrer</button>
          {!readOnly && (
            <button type="button" className={`${styles.secondaryBtn} ${ws.dangerBtn}`} disabled={removing === selected.id}
              title="Supprimer ce marqueur" aria-label="Supprimer ce marqueur" onClick={() => void remove(selected)}>
              {removing === selected.id ? <Loader2 size={13} className={styles.spin} /> : <Trash2 size={13} />}
            </button>
          )}
        </div>
      </form>
      {selected.updatedAt && (
        <p className={lm.selMeta}>Modifié le {fmtDate(selected.updatedAt)}{selected.updatedBy ? ` par ${selected.updatedBy}` : ''}</p>
      )}
    </section>
  );

  const paletteEmpty = search ? (
    <EmptyState compact icon={SearchX} title="Aucun biome ne correspond"
      text={<>Aucun nom ne contient « {q.trim()} ».</>}
      actions={<button type="button" className={styles.secondaryBtn} onClick={() => setQ('')}>Effacer la recherche</button>} />
  ) : (
    <EmptyState compact icon={Trees} title="Aucun biome pour ce niveau"
      text={hiddenBiomes > 0
        ? `${fmtInt(hiddenBiomes)} biome(s) du catalogue sont réservés à d'autres niveaux.`
        : 'Le catalogue des biomes est vide.'}
      actions={!readOnly && (
        <button type="button" className={styles.primaryBtn} onClick={() => setEditBiome('new')}><Plus size={14} /> Nouveau biome</button>
      )} />
  );

  const side = (
    <>
      {!readOnly && (
        <section className={ws.card}>
          <CardTitle icon={Crosshair}>Mode</CardTitle>
          <div className={ws.toolGrid2}>
            {MODES.map((m) => {
              const Icon = m.icon;
              return (
                <button key={m.key} type="button" aria-pressed={mode === m.key}
                  className={cx(ws.toolBtn, ws.toolBtnStack, mode === m.key && ws.toolBtnOn)}
                  title={`${m.hint} (touche ${m.kbd})`} onClick={() => setMode(m.key)}>
                  <Icon size={18} aria-hidden />
                  <span>{m.label}</span>
                  <Kbd>{m.kbd}</Kbd>
                </button>
              );
            })}
          </div>
          <p className={ws.toolHint}>{modeHint}</p>
          {mode === 'place' && !biomeName && (
            <p className={`${ws.toolHint} ${ws.textWarn} ${ws.inlineIcon}`}>
              <TriangleAlert size={12} aria-hidden /> Choisis un biome dans la palette.
            </p>
          )}
        </section>
      )}

      {selectCard}

      <section className={ws.card}>
        <CardTitle icon={Trees}
          actions={!readOnly && <ActionButton icon={Plus} tone="primary" title="Nouveau biome" onClick={() => setEditBiome('new')} />}>
          Biomes
        </CardTitle>
        <SearchField value={q} onChange={setQ} placeholder="Rechercher un biome" />
        {biomesLoading ? (
          <div className={lm.paletteLoading} role="status"><Loader2 size={16} className={ws.spinning} aria-hidden /> Chargement des biomes…</div>
        ) : (
          <div className={ws.countryList}>
            {palette.map((b) => {
              const active = biomeName === b.name;
              const count = countByBiome.get(b.name) ?? 0;
              const label = biomeLabel(b, b.name);
              return (
                <div key={b.name} className={cx(ws.countryRow, lm.paletteRow, active && ws.countryRowOn, !b.enabled && ws.rowOff)}>
                  <button type="button" className={ws.biomePick} disabled={!b.enabled} aria-pressed={active}
                    title={!b.enabled ? 'Biome désactivé : placement impossible'
                      : active ? `${b.name} : cliquer pour désélectionner`
                        : readOnly ? b.name : `${b.name} : choisir pour placer`}
                    onClick={() => pick(b)}>
                    <ColorChip color={b.color} />
                    <BiomeIcon url={b.iconUrl} size={18} keepSpace />
                    <span className={lm.paletteName}>{label}</span>
                    {count > 0 && <span className={ws.countryCount} title="Marqueurs sur ce niveau">{fmtInt(count)}</span>}
                  </button>
                  {!readOnly && <ActionButton icon={Pencil} tone="primary" title={`Modifier ${label}`} onClick={() => setEditBiome(b)} />}
                </div>
              );
            })}
            {palette.length === 0 && paletteEmpty}
          </div>
        )}
        {!biomesLoading && hiddenBiomes > 0 && palette.length > 0 && (
          <p className={lm.paletteNote}>{fmtInt(hiddenBiomes)} biome(s) réservé(s) à d'autres niveaux, masqué(s) ici.</p>
        )}
      </section>

      {orphans.length > 0 && (
        <section className={cx(ws.card, ws.cardWarn)}>
          <CardTitle icon={TriangleAlert}>Marqueurs à nettoyer ({fmtInt(orphans.length)})</CardTitle>
          <p className={ws.toolHint}>Biome absent ou désactivé : ces marqueurs seront ignorés par le jeu.</p>
          <ul className={lm.orphanList}>
            {orphans.map((m) => {
              const b = biomeMap.get(m.biomeName);
              return (
                <li key={m.id}>
                  <button type="button" className={lm.orphanBtn} title="Sélectionner et centrer"
                    onClick={() => { setSelectedId(m.id); focus(m); }}>
                    <ColorChip color={b?.color ?? ABSENT_COLOR} />
                    <span className={lm.orphanName}>{biomeLabel(b, m.biomeName)}</span>
                    <span className={lm.coordsText}>{m.x}, {m.z}</span>
                  </button>
                  <span className={lm.orphanWhy}>{b ? 'désactivé' : 'absent'}</span>
                  {!readOnly && (
                    <ActionButton icon={Trash2} tone="danger" title="Supprimer ce marqueur" busy={removing === m.id}
                      onClick={() => void remove(m)} />
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <section className={ws.card}>
        <CardTitle icon={Layers}>Affichage</CardTitle>
        <div className={lm.layerList}>
          {wm.staticBg && (
            <button type="button" className={cx(ws.layerToggle, showBackground && ws.layerToggleOn)} aria-pressed={showBackground}
              title={`Image fixe du terrain sous les pays, en attendant les tuiles du niveau${wm.staticBgState === 'error' ? ' (image introuvable : fond neutre)' : ''}`}
              onClick={toggleBackground}>
              {showBackground ? <Eye size={14} aria-hidden /> : <EyeOff size={14} aria-hidden />} Fond de carte
            </button>
          )}
          <button type="button" className={cx(ws.layerToggle, showCountries && ws.layerToggleOn)} aria-pressed={showCountries}
            onClick={toggleCountries}>
            {showCountries ? <Eye size={14} aria-hidden /> : <EyeOff size={14} aria-hidden />} Afficher les pays
          </button>
        </div>
      </section>
    </>
  );

  const overlayHint = readOnly
    ? 'Lecture seule : clic sur un marqueur pour voir ses détails.'
    : mode === 'place'
      ? placeBiome ? `Clic sur la carte pour placer « ${biomeLabel(placeBiome, placeBiome.name)} ».` : 'Choisis un biome dans la palette à droite.'
      : 'Glisser un marqueur pour le déplacer, clic pour le sélectionner.';

  return (
    <>
      <MapFrame wm={wm} side={side} onEscape={onEscape}
        overlay={(
          <>
            {/* Pastilles d'état au-dessus de l'encart (le haut de la carte porte déjà la note du fond). */}
            <div className={ws.mapBottomLeft}>
              {markersLoading && (
                <span className={ws.busyBadge} role="status"><Loader2 size={12} className={ws.spinning} aria-hidden /> Chargement des marqueurs…</span>
              )}
              {pending > 0 && (
                <span className={ws.busyBadge} role="status"><Loader2 size={12} className={ws.spinning} aria-hidden /> En cours… ({pending})</span>
              )}
              <div className={ws.mapInfoCard}>
                <span className={ws.mapInfoHead}>
                  <Trees size={14} aria-hidden />
                  <span><strong>{markersLoading ? '…' : fmtInt(markers.length)}</strong> marqueur(s) sur {levelTitle}</span>
                </span>
                <span>{overlayHint}</span>
              </div>
            </div>
          </>
        )} />
      {editBiome && (
        <BiomeModal biome={editBiome === 'new' ? null : editBiome} levels={levels}
          onClose={() => setEditBiome(null)} onSaved={putBiome}
          onDeleted={(name) => { dropBiome(name); if (biomeName === name) setBiomeName(null); }} />
      )}
    </>
  );
}
